import React from 'react';
import { motion } from 'framer-motion';
import {
    Users,
    UserCheck,
    UserMinus,
    Target,
    TrendingUp
} from 'lucide-react';

export const Dashboard = ({ stats }) => {
    // Конфигурация карточек для удобного рендеринга
    const cards = [
        {
            title: "Всего студентов",
            value: stats.totalStudents,
            icon: Users,
            color: "blue",
            description: "Общее число в базе"
        },
        {
            title: "Подтверждено",
            value: stats.confirmedStudents,
            icon: UserCheck,
            color: "emerald",
            description: "Прошли верификацию"
        },
        {
            title: "Не подтверждено",
            value: stats.unconfirmedStudents,
            icon: UserMinus,
            color: "amber",
            description: "Ожидают проверки"
        },
        {
            title: "План на сегодня",
            value: stats.todayConfirmed,
            target: stats.todayTarget,
            icon: Target,
            color: "purple",
            isProgress: true
        }
    ];

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {cards.map((card, index) => (
                <StatCard key={index} {...card} index={index} />
            ))}
        </div>
    );
};

const StatCard = ({ title, value, target, icon: Icon, color, description, isProgress, index }) => {
    // Маппинг цветов для Tailwind
    const theme = {
        blue: "text-blue-400 bg-blue-500/10 border-blue-500/20 shadow-blue-500/5",
        emerald: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20 shadow-emerald-500/5",
        amber: "text-amber-400 bg-amber-500/10 border-amber-500/20 shadow-amber-500/5",
        purple: "text-purple-400 bg-purple-500/10 border-purple-500/20 shadow-purple-500/5",
    };

    // Расчет процента для прогресс-бара
    const percentage = isProgress ? Math.min(Math.round((value / target) * 100), 100) : 0;

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ y: -5 }}
            className="relative overflow-hidden group p-5 bg-slate-900/40 backdrop-blur-md border border-white/5 rounded-[24px] shadow-2xl"
        >
            {/* Декоративное свечение в углу */}
            <div className={`absolute -right-4 -top-4 w-24 h-24 blur-3xl opacity-10 transition-opacity group-hover:opacity-20 ${theme[color]}`} />

            <div className="flex justify-between items-start mb-4">
                <div className={`p-3 rounded-xl border ${theme[color]}`}>
                    <Icon size={22} />
                </div>
                {/* Небольшой бейдж тренда (визуальный эффект) */}
                <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-white/5 px-2 py-1 rounded-lg">
                    <TrendingUp size={10} /> Live
                </div>
            </div>

            <div>
                <p className="text-sm font-medium text-slate-400 mb-1">{title}</p>
                <div className="flex items-baseline gap-2">
                    <h3 className="text-3xl font-black text-white tracking-tight">
                        {isProgress ? value : value}
                    </h3>
                    {isProgress && (
                        <span className="text-slate-500 font-medium">/ {target}</span>
                    )}
                </div>
            </div>

            {isProgress ? (
                <div className="mt-4">
                    <div className="flex justify-between text-[10px] mb-1.5 uppercase font-bold tracking-widest text-slate-500">
                        <span>Прогресс</span>
                        <span>{percentage}%</span>
                    </div>
                    <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
                        <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${percentage}%` }}
                            transition={{ duration: 1, ease: "easeOut" }}
                            className={`h-full rounded-full bg-gradient-to-r from-purple-500 to-indigo-500`}
                        />
                    </div>
                </div>
            ) : (
                <p className="mt-4 text-xs text-slate-500 font-medium flex items-center gap-1">
                    {description}
                </p>
            )}
        </motion.div>
    );
};